// settingsPlanPaneUtils.js

// Services
import { LIVE_ROOMS_SURFACE_ENABLED } from 'features/liveRoomsSurface';
import { PLAN_PRICING_MODEL } from 'services/plans/PlanService';
import { computeEffectiveLimitsFromPlan } from 'services/bases/BaseEntitlementsService';

function formatPlanCurrencyFromCents(seatPriceCents, currency = 'USD') {
    const normalizedCents = Number(seatPriceCents || 0);
    if (!Number.isFinite(normalizedCents) || normalizedCents < 0) {
        return '$0';
    }

    const hasCents = normalizedCents % 100 !== 0;

    return new Intl.NumberFormat('en-US', {
        currency: String(currency || 'USD').trim().toUpperCase() || 'USD',
        maximumFractionDigits: hasCents ? 2 : 0,
        minimumFractionDigits: hasCents ? 2 : 0,
        style: 'currency',
    }).format(normalizedCents / 100);
}

export function formatPlanPrice(plan) {
    if (!plan) return 'No plan assigned';

    const pricingModel = String(plan.pricingModel || '').trim();
    const seatPrice = Number(plan.seatPrice || 0);

    if (pricingModel === PLAN_PRICING_MODEL.FREE || seatPrice <= 0) {
        return 'Free';
    }

    return `${formatPlanCurrencyFromCents(seatPrice, plan.currency)} / seat per month`;
}

/**
 * getPlanCheckoutChargeSummary
 *
 * Builds the per-seat checkout estimate shown before subscription creation.
 *
 * @param {object} params - Charge inputs.
 * @param {object} params.plan - Selected paid plan row.
 * @param {number} [params.memberCount] - Current base member count.
 * @param {number} [params.pendingInviteCount] - Pending space invite count.
 * @param {number} [params.billedSeatCount] - Optional billed seat override (e.g. from billing record).
 *
 * @returns {object|null} Checkout charge summary, or null when plan pricing is unavailable.
 */
export function getPlanCheckoutChargeSummary({
    plan,
    memberCount = 0,
    pendingInviteCount = 0,
    billedSeatCount: billedSeatCountOverride = null,
}) {
    if (!plan) return null;

    const pricingModel = String(plan.pricingModel || '').trim();
    const seatPriceCents = Number(plan.seatPrice || 0);

    if (pricingModel === PLAN_PRICING_MODEL.FREE || seatPriceCents <= 0) {
        return null;
    }

    const normalizedMemberCount = Math.max(0, Math.floor(Number(memberCount || 0)));
    const normalizedPendingInviteCount = Math.max(0, Math.floor(Number(pendingInviteCount || 0)));
    const reservedSeatCount = normalizedMemberCount + normalizedPendingInviteCount;
    const normalizedBilledSeatOverride = Math.floor(Number(billedSeatCountOverride));
    const billedSeatCount = Number.isFinite(normalizedBilledSeatOverride) && normalizedBilledSeatOverride > 0
        ? normalizedBilledSeatOverride
        : Math.max(1, reservedSeatCount);
    const monthlyTotalCents = seatPriceCents * billedSeatCount;
    const currency = String(plan.currency || 'USD').trim().toUpperCase() || 'USD';
    const seatPriceText = formatPlanCurrencyFromCents(seatPriceCents, currency);
    const monthlyTotalText = formatPlanCurrencyFromCents(monthlyTotalCents, currency);
    const seatLabel = `${billedSeatCount} seat${billedSeatCount === 1 ? '' : 's'}`;
    const memberLabel = `${normalizedMemberCount} member${normalizedMemberCount === 1 ? '' : 's'}`;

    let seatsDetailText = memberLabel;
    if (normalizedPendingInviteCount > 0) {
        const inviteLabel = `${normalizedPendingInviteCount} pending invite${normalizedPendingInviteCount === 1 ? '' : 's'}`;
        seatsDetailText = `${memberLabel} • ${inviteLabel}`;
    }

    return {
        billedSeatCount,
        currency,
        memberCount: normalizedMemberCount,
        monthlyTotalCents,
        monthlyTotalText,
        pendingInviteCount: normalizedPendingInviteCount,
        reservedSeatCount,
        seatLabel,
        seatPriceCents,
        seatPriceText,
        seatsDetailText,
    };
}

/**
 * formatPlanBillingMonthlyTotalText
 *
 * Builds the recurring subscription total shown in base billing settings.
 *
 * @param {object} params - Billing display inputs.
 * @param {object} params.plan - Active space plan row.
 * @param {number} [params.seatCount] - Seats billed on the subscription.
 *
 * @returns {string|null} Monthly total line item, or null when unavailable.
 */
export function formatPlanBillingMonthlyTotalText({ plan, seatCount = 0 }) {
    const normalizedSeatCount = Math.max(0, Math.floor(Number(seatCount || 0)));
    if (normalizedSeatCount < 1) {
        return null;
    }

    const summary = getPlanCheckoutChargeSummary({
        billedSeatCount: normalizedSeatCount,
        plan,
    });

    if (!summary) {
        return null;
    }

    return `${summary.billedSeatCount} × ${summary.seatPriceText} — ${summary.monthlyTotalText} / month`;
}

/**
 * getPlanCheckoutEffectivePools
 *
 * Builds scaled base pool summaries for checkout review.
 *
 * @param {object} params - Pool inputs.
 * @param {number} params.billedSeatCount - Seats billed at checkout.
 * @param {object} params.plan - Selected paid plan row.
 *
 * @returns {Array<{detail: string, label: string, value: string}>} Effective pool rows.
 */
export function getPlanCheckoutEffectivePools({ plan, billedSeatCount = 1 }) {
    if (!plan) return [];

    const seats = Math.max(1, Math.floor(Number(billedSeatCount || 1)));
    const { limits } = computeEffectiveLimitsFromPlan({
        billedSeatCount: seats,
        plan,
    });
    const pools = [];

    const storageBytesPerSeat = resolvePlanStorageBytesPerSeat(plan);
    if (storageBytesPerSeat) {
        pools.push({
            detail: `${formatBytes(storageBytesPerSeat)} per seat × ${seats} seat${seats === 1 ? '' : 's'}`,
            label: 'Storage pool',
            value: formatBytes(limits.storageBytes),
        });
    }

    const apiPerSeat = Math.max(0, Math.floor(Number(
        plan.apiMonthlyRunLimitPerSeat
        || plan.apiMonthlyRunLimit
        || 0
    )));
    if (plan.apiEnabled && apiPerSeat > 0) {
        pools.push({
            detail: `${formatWholeCount(apiPerSeat)} per seat × ${seats} seat${seats === 1 ? '' : 's'}`,
            label: 'API runs / month',
            value: formatWholeCount(limits.apiMonthlyRunLimit),
        });
    }

    // Live minutes — restore with LIVE_ROOMS_SURFACE_ENABLED
    if (LIVE_ROOMS_SURFACE_ENABLED) {
        const liveParticipantMinutesPerSeat = Math.max(0, Math.floor(Number(
            plan.liveParticipantMinutesPerSeat
            || plan.liveParticipantMinutesMonthlyCap
            || 0
        )));
        if (plan.liveEnabled && liveParticipantMinutesPerSeat > 0) {
            pools.push({
                detail: `${formatWholeCount(liveParticipantMinutesPerSeat)} per seat × ${seats} seat${seats === 1 ? '' : 's'}`,
                label: 'Live minutes / month',
                value: formatWholeCount(limits.liveParticipantMinutesMonthlyCap),
            });
        }
    }

    return pools;
}

export function formatBytes(bytes) {
    const numericBytes = Number(bytes || 0);
    if (!Number.isFinite(numericBytes) || numericBytes < 1) {
        return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const unitIndex = Math.min(
        Math.floor(Math.log(numericBytes) / Math.log(1024)),
        units.length - 1
    );
    const value = numericBytes / (1024 ** unitIndex);
    const formattedValue = value >= 10 || unitIndex === 0
        ? value.toFixed(0)
        : value.toFixed(1);

    return `${formattedValue} ${units[unitIndex]}`;
}

/**
 * buildBaseThresholdAlerts
 *
 * Builds active threshold warnings for space admins from current usage.
 *
 * @param {object} params - Alert inputs.
 * @returns {Array<object>} Active threshold alerts.
 */
export function buildBaseThresholdAlerts({
    apiRequestCount = 0,
    apiRunLimit = 0,
    liveParticipantMinutesCap = 0,
    liveParticipantMinutesUsed = 0,
    storageBytesEffective = 0,
    storageBytesLimit = 0,
    teamUsagePercentUsed = null,
} = {}) {
    const alerts = [];

    const pushAlert = ({ limitType, percent, threshold }) => {
        alerts.push({
            limitType,
            percent,
            severity: threshold >= 100 ? 'critical' : 'warning',
            threshold,
        });
    };

    const storageLimit = Math.max(0, Math.floor(Number(storageBytesLimit || 0)));
    const storageUsed = Math.max(0, Math.floor(Number(storageBytesEffective || 0)));
    if (storageLimit > 0) {
        const percent = Math.min(100, Math.round((storageUsed / storageLimit) * 100));
        if (percent >= 100) {
            pushAlert({ limitType: 'storage', percent, threshold: 100 });
        } else if (percent >= 80) {
            pushAlert({ limitType: 'storage', percent, threshold: 80 });
        }
    }

    const apiLimit = Math.max(0, Math.floor(Number(apiRunLimit || 0)));
    const apiUsed = Math.max(0, Math.floor(Number(apiRequestCount || 0)));
    if (apiLimit > 0) {
        const percent = Math.min(100, Math.round((apiUsed / apiLimit) * 100));
        if (percent >= 100) {
            pushAlert({ limitType: 'api', percent, threshold: 100 });
        } else if (percent >= 80) {
            pushAlert({ limitType: 'api', percent, threshold: 80 });
        }
    }

    const liveLimit = Math.max(0, Math.floor(Number(liveParticipantMinutesCap || 0)));
    const liveUsed = Math.max(0, Math.floor(Number(liveParticipantMinutesUsed || 0)));
    if (liveLimit > 0) {
        const percent = Math.min(100, Math.round((liveUsed / liveLimit) * 100));
        if (percent >= 100) {
            pushAlert({ limitType: 'live', percent, threshold: 100 });
        } else if (percent >= 80) {
            pushAlert({ limitType: 'live', percent, threshold: 80 });
        }
    }

    if (teamUsagePercentUsed != null) {
        const percent = Math.min(100, Math.max(0, Math.floor(Number(teamUsagePercentUsed || 0))));
        if (percent >= 100) {
            pushAlert({ limitType: 'teamUsage', percent, threshold: 100 });
        } else if (percent >= 80) {
            pushAlert({ limitType: 'teamUsage', percent, threshold: 80 });
        }
    }

    return alerts.sort((left, right) => right.threshold - left.threshold || right.percent - left.percent);
}

export function resolvePlanStorageBytesPerSeat(plan) {
    const perSeat = Number(plan?.storageBytesPerSeat || plan?.pooledStorageBytes || 0);
    if (!Number.isFinite(perSeat) || perSeat < 1) {
        return null;
    }

    return Math.floor(perSeat);
}

export function formatStoragePerSeat(plan) {
    const perSeat = resolvePlanStorageBytesPerSeat(plan);
    if (!perSeat) {
        return 'Not included';
    }

    return `${formatBytes(perSeat)} / seat`;
}

export function formatUserLimit(plan) {
    const pricingModel = String(plan?.pricingModel || '').trim();
    if (pricingModel === PLAN_PRICING_MODEL.PER_SEAT) {
        return 'Unlimited users';
    }

    const maxUsers = Number(plan?.maxUsers);
    if (!Number.isFinite(maxUsers) || maxUsers < 1) {
        return 'Unlimited users';
    }

    return `${Math.floor(maxUsers)} users`;
}

function getPlanUserLimitScore(plan) {
    const maxUsers = Number(plan?.maxUsers);
    if (!Number.isFinite(maxUsers) || maxUsers < 1) {
        return Infinity;
    }

    return maxUsers;
}

/**
 * comparePlanTiers
 *
 * Compares two plans by pooled storage, then user limits.
 *
 * @returns {number} Positive when target is higher tier, negative when lower, zero when equal.
 */
export function comparePlanTiers(targetPlan, currentPlan) {
    if (!targetPlan || !currentPlan) return 0;

    const storageDiff = Number(resolvePlanStorageBytesPerSeat(targetPlan) || 0)
        - Number(resolvePlanStorageBytesPerSeat(currentPlan) || 0);
    if (storageDiff !== 0) {
        return storageDiff > 0 ? 1 : -1;
    }

    const userDiff = getPlanUserLimitScore(targetPlan) - getPlanUserLimitScore(currentPlan);
    if (userDiff !== 0) {
        return userDiff > 0 ? 1 : -1;
    }

    return 0;
}

/**
 * getPlanActionLabel
 *
 * Returns the primary action label for a catalog plan card.
 */
export function getPlanActionLabel({
    currentPlan,
    hasActivePaidSubscription,
    isCurrent,
    isSaving,
    isSelectable,
    targetPlan,
}) {
    if (isCurrent) return 'Current Plan';
    if (isSaving) return 'Updating...';
    if (hasActivePaidSubscription && !isSelectable) return 'Billing Change Soon';

    if (hasActivePaidSubscription && isSelectable) {
        return 'Downgrade';
    }

    const tierComparison = comparePlanTiers(targetPlan, currentPlan);
    if (tierComparison > 0) return 'Upgrade';
    if (tierComparison < 0) return 'Downgrade';

    return isSelectable ? 'Select Plan' : 'Subscribe';
}

export function formatApiSummary(plan) {
    if (!plan?.apiEnabled) return 'Not included';

    const monthlyRuns = Number(plan.apiMonthlyRunLimitPerSeat || plan.apiMonthlyRunLimit || 0);
    if (!Number.isFinite(monthlyRuns) || monthlyRuns < 1) {
        return 'Included';
    }

    return `${Math.floor(monthlyRuns).toLocaleString()} runs / seat / month`;
}

export function formatLiveSummary(plan) {
    if (!plan?.liveEnabled) return 'Not included';

    const participantMinutes = Math.max(0, Math.floor(Number(
        plan.liveParticipantMinutesPerSeat || plan.liveParticipantMinutesMonthlyCap || 0
    )));
    const roomCount = Math.max(1, Math.floor(Number(plan.liveConcurrentRoomsCap || 1)));
    const participantCount = Math.max(0, Math.floor(Number(plan.liveMaxParticipantsPerRoom || 0)));

    return `${participantMinutes.toLocaleString()} mins • ${roomCount} room${roomCount === 1 ? '' : 's'} • ${participantCount} / room`;
}

export function formatRecordingSummary(plan) {
    if (!plan?.liveEnabled || !plan?.liveRecordingEnabled) return 'Not included';

    const recordingMinutes = Math.max(0, Math.floor(Number(
        plan.liveEgressMinutesPerSeat || plan.liveEgressMinutesMonthlyCap || 0
    )));
    return `${recordingMinutes.toLocaleString()} mins / seat / month`;
}

export function isSelfServeSelectable(plan) {
    if (!plan) return false;

    const pricingModel = String(plan.pricingModel || '').trim();
    const seatPrice = Number(plan.seatPrice || 0);
    return pricingModel === PLAN_PRICING_MODEL.FREE || seatPrice <= 0;
}

export function getDefaultFreePlan(plans) {
    return (Array.isArray(plans) ? plans : []).find((plan) => plan?.isDefault === true) || null;
}

export function formatBillingStatus(status) {
    const normalized = String(status || '').trim();
    if (!normalized) return 'Not started';

    return normalized
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

export function formatDateTime(value) {
    if (!value) return '—';

    if (value instanceof Date) {
        return value.toLocaleString();
    }

    if (typeof value?.toDate === 'function') {
        const dateValue = value.toDate();
        if (dateValue instanceof Date) {
            return dateValue.toLocaleString();
        }
    }

    return '—';
}

export function formatBillingPeriodEndLabel(billingRecord) {
    return billingRecord?.cancelAtPeriodEnd ? 'Cancels on' : 'Billing renews';
}

export function formatSubscriptionCancelScheduledMessage({
    billingRecord,
    planTitle,
}) {
    if (!billingRecord?.cancelAtPeriodEnd) {
        return '';
    }

    const cancelDateText = formatDateTime(billingRecord?.currentPeriodEnd);
    const cleanedPlanTitle = String(planTitle || billingRecord?.planTitle || '').trim();

    if (cancelDateText === '—') {
        return cleanedPlanTitle
            ? `${cleanedPlanTitle} remains active until the end of the current billing period.`
            : 'Cancellation is scheduled for the end of the current billing period.';
    }

    return cleanedPlanTitle
        ? `Cancellation scheduled for ${cancelDateText}. ${cleanedPlanTitle} remains active until then.`
        : `Cancellation scheduled for ${cancelDateText}.`;
}

export function formatSubscriptionCancelConfirmMessage({
    downgradePlanTitle,
    isDowngradeConfirm,
    periodEnd,
}) {
    const cancelDateText = formatDateTime(periodEnd);

    if (isDowngradeConfirm) {
        const targetPlanTitle = String(downgradePlanTitle || 'the free plan').trim();

        if (cancelDateText === '—') {
            return `Cancel billing at the end of the current period and switch this space to ${targetPlanTitle} afterward? You'll keep your current plan until the period ends.`;
        }

        return `You will keep your current plan until ${cancelDateText}.`;
    }

    if (cancelDateText === '—') {
        return 'Cancel billing at the end of the current period? You\'ll keep your current plan until the period ends.';
    }

    return `Cancel billing on ${cancelDateText}? You'll keep your current plan until then.`;
}

export function formatMonthLabel(monthKey) {
    const normalized = String(monthKey || '').trim();
    if (!normalized) return 'Current month';

    const match = normalized.match(/^(\d{4})-(\d{2})$/);
    if (!match) return normalized;

    const [, year, month] = match;
    const dateValue = new Date(Date.UTC(Number(year), Number(month) - 1, 1));

    return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        timeZone: 'UTC',
        year: 'numeric',
    }).format(dateValue);
}

export function formatWholeCount(value) {
    const parsed = Number(value || 0);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return '0';
    }

    return Math.floor(parsed).toLocaleString();
}

export function formatUsageValue({
    formatter = formatWholeCount,
    limit,
    used,
}) {
    const safeLimit = Number(limit || 0);
    const usedText = formatter(used);

    if (!Number.isFinite(safeLimit) || safeLimit < 1) {
        return `${usedText} used`;
    }

    return `${usedText} / ${formatter(safeLimit)}`;
}

export function secondsToWholeMinutes(value) {
    const parsed = Number(value || 0);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return 0;
    }

    return Math.max(0, Math.floor(parsed / 60));
}